<template>
  <Txt class="mt-4 flex items-center gap-2 text-base font-medium">{{ $t('Company.CompanyCreation') }}</Txt>
  <p class="text-sm text-gray-600">{{ $t('Company.CompanyCreationDescription') }}</p>

  <div v-if="setup" class="space-y-8 pt-6">
    <div class="cyber-card flex flex-col gap-6 rounded-xl border">
      <div class="grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 pt-6 grid-cols-[1fr_auto] [.border-b]:pb-6">
        <h4 class="leading-none flex items-center justify-between">
          <div class="flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'gear']" class="text-blue-800 w-7 !h-7" :class="{ 'animate-spin': loading }" />
            <span>{{ $t('Company.CreationProgress') }}</span>
          </div>
          <div class="text-sm">{{ completedSteps }}/{{ steps.length }} {{ $t('General.Completed') }}</div>
        </h4>
      </div>
      <div class="px-6 [&:last-child]:pb-6 space-y-6">
        <div class="space-y-2">
          <div class="flex justify-between text-sm">
            <span>{{ $t('Company.TotalProgress') }}</span>
            <span>{{ Math.round(progress) }}%</span>
          </div>
          <div aria-valuemax="100" aria-valuemin="0" role="progressbar" data-state="indeterminate" data-max="100"
            data-slot="progress" class="bg-blue-600 relative w-full overflow-hidden rounded-full h-3">
            <div data-state="indeterminate" data-max="100" data-slot="progress-indicator"
              class="bg-gray-200 h-full w-full flex-1 transition-all" 
              :style="{ transform: 'translateX(' + progress + '%)' }"></div>
          </div>
        </div>
        <div class="space-y-4">
          <div v-for="step in steps" :key="step.id" class="flex items-center space-x-4">
            <div class="w-10 h-10 rounded-full flex items-center justify-center transition-all"
              :class="stepClass(step)">
              <Icon v-if="showCheck(step)" name="check" />
              <font-awesome-icon v-else-if="showSpinner(step)" :icon="['fas', 'rotate-right']" />
              <font-awesome-icon v-else-if="showError(step)" :icon="['fas', 'xmark']" />
              <font-awesome-icon v-else :icon="['fas', step.icon]" class="w-5 h-5" />
            </div>
            <div class="flex-1">
              <div class="flex items-center space-x-2">
                <h4 class="font-medium">{{ $t('Company.Step' + step.id) }}</h4>
              </div>
              <p class="text-sm">{{ $t('Company.Step' + step.id + 'Description') }}</p>
              <p v-if="stepHasError(step)" class="text-sm text-red-600 mt-1">
                <font-awesome-icon :icon="['fas', 'xmark']" />
                {{ Array.isArray(status['step' + step.id].error) ? status['step' + step.id].error.join(', ') : status['step' + step.id].error }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <CGBanner 
      v-if="!loading && stepFailed" 
      class="mt-6"
      :title="$t('Company.CreateError')" 
      :subtitle="$t('Company.' + (companyCreationFailed? 'CreateDisasterDescription': 'CreateErrorDescription'))" 
      variant="red">

      <template #icon>
        <Icon name="warning" size="22" class="text-red-600" />
      </template>
    </CGBanner>

    <div v-if="!loading && success" class="flex justify-center space-x-4">
      <div class="flex items-center space-x-2 text-green-600">
        <font-awesome-icon :icon="['fas','circle-check']" class="!w-5 h-5" />
        <span class="text-lg font-semibold">{{ $t('Company.CreateCompanySuccess') }}</span>
      </div>
    </div>

    <CGBanner 
      v-if="!loading && !stepFailed" 
      class="mt-6"
      :title="$t('Company.CompanyCreated')" 
      :subtitle="$t('Company.CompanyCreatedDescription')" 
      variant="green">

      <template #icon>
        <Icon name="check" size="22" class="text-green-600" />
      </template>
    </CGBanner>
  </div>

  <div v-else class="space-y-6 pt-10">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

      <!-- General Info Recap Card -->
      <div class="bg-card flex flex-col gap-6 rounded-xl border cyber-card">
        <div class="auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6 py-6 flex flex-row items-center justify-between">
          <h4 class="leading-none flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'building']" class="text-blue-800 w-5 h-5" />
            <span>{{ $t('Company.GeneralInfo') }}</span>
          </h4>
          <div
            class="inline-flex items-center cursor-pointer justify-center text-sm font-medium text-blue-600 hover:text-blue-800 h-8 px-3"
            @click="$emit('move', 1)">
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-blue-800 w-4 h-4 mx-1" />
            <span>{{ $t('General.Edit') }}</span>
          </div>
        </div>
        <div class="px-6 [&:last-child]:pb-6 space-y-3 text-sm">
          <div class="flex justify-between">
            <span>{{ $t('Navbar.Company') }}</span>
            <span class="font-medium">{{ company.displayname }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t('General.LegalCompanyName') }}</span>
            <span class="font-medium">{{ company.legal_company_name }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t('General.Country') }}</span>
            <span class="font-medium">{{ countryOptions[company.country] }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t('Company.Subdomain') }}</span>
            <span class="font-medium font-mono text-blue-600">{{ company.name }}</span>
          </div>
          <div class="flex justify-between items-start">
            <span>{{ $t('Company.SupportedLanguages') }}</span>
            <div class="flex flex-wrap gap-1 max-w-[200px] justify-end">
              <span v-for="l in company.supported_languages" :key="l" class="inline-flex items-center justify-center rounded-md px-2 py-0.5 font-medium w-fit 
                  whitespace-nowrap shrink-0 gap-1 bg-gray-200 text-secondary-foreground text-xs">{{ l.label }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Customization Recap Card -->
      <div class="bg-card flex flex-col gap-6 rounded-xl border cyber-card">
        <div class="auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6 py-6 flex flex-row items-center justify-between">
          <h4 class="leading-none flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'palette']" class="text-pink-800 w-5 h-5" />
            <span>{{ $t('Company.Customization') }}</span>
          </h4>
          <div
            class="inline-flex items-center cursor-pointer justify-center text-sm font-medium text-pink-600 hover:text-pink-800 h-8 px-3"
            @click="$emit('move', 2)">
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-pink-800 w-4 h-4 mx-1" />
            <span>{{ $t('General.Edit') }}</span>
          </div>
        </div>
        <!-- Company Logos -->
        <div class="px-6 [&:last-child]:pb-6 space-y-3 text-sm">
          <div class="flex justify-between">
            <span>{{ $t('Company.ToggleLogo') }}</span>
            <span class="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium w-fit 
              whitespace-nowrap shrink-0 gap-1 overflow-hidden"
              :class="[{ 'bg-secondary text-secondary-foreground': !company.toggleImage }, { 'bg-blue-500 text-white': company.toggleImage }]">
              {{ company.toggleImage ? $t('Company.LogoAvailable') : $t('Company.LogoNotAvailable') }}
            </span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t('Company.MenuLogo') }}</span>
            <span class="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-medium w-fit 
              whitespace-nowrap shrink-0 gap-1 overflow-hidden"
              :class="[{ 'bg-secondary text-secondary-foreground': !company.logo }, { 'bg-blue-500 text-white': company.logo }]">
              {{ company.logo ? $t('Company.LogoAvailable') : $t('Company.LogoNotAvailable') }}
            </span>
          </div>
          <!-- Company colors -->
          <div class="flex justify-between items-center">
            <span>{{ $t('Company.PrimaryColor') }}</span>
            <div class="flex items-center space-x-2">
              <div class="w-4 h-4 rounded border" :style="`background-color: ${company.primaryColor};`"></div>
              <span class="font-mono text-xs">{{ company.primaryColor }}</span>
            </div>
          </div>
          <div class="flex justify-between items-center">
            <span>{{ $t('Company.SecondaryColor') }}</span>
            <div class="flex items-center space-x-2">
              <div class="w-4 h-4 rounded border" :style="`background-color: ${company.secondaryColor};`"></div>
              <span class="font-mono text-xs">{{ company.secondaryColor }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Auth Recap Card -->
      <div class="bg-card flex flex-col gap-6 rounded-xl border cyber-card">
        <div class="auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6 py-6 flex flex-row items-center justify-between">
          <h4 class="leading-none flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'key']" class="text-purple-800 w-5 h-5" />
            <span>{{ $t('Company.Authentication') }}</span>
          </h4>
          <div
            class="inline-flex items-center cursor-pointer justify-center text-sm font-medium text-purple-600 hover:text-purple-800 h-8 px-3"
            @click="$emit('move', 3)">
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-purple-800 w-4 h-4 mx-1" />
            <span>{{ $t('General.Edit') }}</span>
          </div>
        </div>
        <div class="px-6 [&:last-child]:pb-6 space-y-3 text-sm">
          <div class="flex justify-between">
            <span>{{ $t('Dashboard.Charts.Type') }}</span>
            <span class="font-medium">{{ $t('Company.' + company.authType) }}</span>
          </div>
          <div v-if="company.useMetadataXML" class="flex justify-between">
            <span>{{ $t('Company.MetadataXML') }}</span>
            <span class="font-medium">{{ company.useMetadataXML ? $t('General.Yes') : $t('General.No') }}</span>
          </div>
        </div>
      </div>

      <!-- User management Recap Card -->
      <div class="bg-card flex flex-col gap-6 rounded-xl border cyber-card">
        <div class="auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6 py-6 flex flex-row items-center justify-between">
          <h4 class="leading-none flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'users']" class="text-green-800 w-5 h-5" />
            <span>{{ $t('Company.UserManagement') }}</span>
          </h4>
          <div
            class="inline-flex items-center cursor-pointer justify-center text-sm font-medium text-green-600 hover:text-green-800 h-8 px-3"
            @click="$emit('move', 4)">
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-green-800 w-4 h-4 mx-1" />
            <span>{{ $t('General.Edit') }}</span>
          </div>
        </div>
        <div class="px-6 [&:last-child]:pb-6 space-y-3 text-sm">
          <div class="flex justify-between">
            <span>{{ $t('Company.ProvisioningType') }}</span>
            <span class="font-medium">{{ $t('Company.ProvisioningType' + company.provisioningType) }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ $t('Company.Gamification') }}</span>
            <span class="font-medium">{{ $t('Company.' + (company.enableGamification ? 'Enabled' : 'Disabled')) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6">
      <!-- License management Recap Card -->
      <div v-if="totalLicenses > 0" class="bg-card flex flex-col gap-6 rounded-xl border cyber-card">
        <div class="auto-rows-min grid-rows-[auto_auto] gap-1.5 px-6 py-6 flex flex-row items-center justify-between">
          <h4 class="leading-none flex items-center space-x-2">
            <font-awesome-icon :icon="['fas', 'box']" class="text-indigo-800 w-5 h-5" />
            <span>{{ $t('Company.Licenses') }}</span>
          </h4>
          <div
            class="inline-flex items-center cursor-pointer justify-center text-sm font-medium text-indigo-600 hover:text-indigo-800 h-8 px-3"
            @click="$emit('move', 5)">
            <font-awesome-icon :icon="['fas', 'pencil']" class="text-indigo-800 w-4 h-4 mx-1" />
            <span>{{ $t('General.Edit') }}</span>
          </div>
        </div>
        <div class="px-6 pb-6 text-sm">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ActiveServiceCard v-for="service in company.services.filter(s => s.enabled)" :key="service.id" v-bind="service" />
          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import { defineComponent } from 'vue';
import { ProvisioningType, AuthenticationType } from '@/common/constants';

export default defineComponent({
  props: {
    company: {
      type: Object,
      required: true
    },
    setup: {
      type: Boolean,
      default: false
    },
    status: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['move'],
  data() {
    return {
      ProvisioningType,
      AuthenticationType,
      countryOptions: {},
      steps: [
        { id: 1, icon: 'check' },
        { id: 2, icon: 'palette' },
        { id: 3, icon: 'key' },
        { id: 4, icon: 'user' },
        { id: 5, icon: 'box' }
      ]
    }
  },
  computed: {
    totalLicenses() {
      return this.company.services.reduce((total, service) => {
        return total + (service.enabled ? parseInt(service.quantity, 10) : 0);
      }, 0);
    },
    activeServices() {
      return this.company.services.filter(s => s.enabled).length;
    },
    loading() {
      return this.steps.some(step => this.status['step' + step.id] && this.status['step' + step.id].loading);
    },
    success() {
      return this.steps.every(step => this.status['step' + step.id] && this.status['step' + step.id].success);
    },
    companyCreationFailed() {
      return this.status.step1.success === false;
    },
    stepFailed() {
      return this.steps.some(step => {
        const s = this.status['step' + step.id];
        return s && s.error && s.error.length > 0;
      });
    },
    completedSteps() {
      return this.steps.filter(step => this.status['step' + step.id] && this.status['step' + step.id].success).length;
    },
    progress() {
      return (this.completedSteps / this.steps.length) * 100;
    },
    activeStep() {
      for (const step of this.steps) {
        const s = this.status['step' + step.id];
        if (s && (s.loading || s.success === false)) {
          return step.id;
        }
      }
      // If all are successful, return a value greater than the last step
      return Math.max(...this.steps.map(s => s.id)) + 1;
    }
  },
  methods: {
    stepClass(step) {
      const s = this.status['step' + step.id];
      if (!s) return 'bg-gray-300 text-gray-600';
      if (step.id < this.activeStep) {
        if (s.success) return 'bg-green-600 text-white';
        if (s.success === false) return 'bg-red-600 text-white';
      }
      if (step.id === this.activeStep) {
        if (s.loading) return 'bg-blue-600 text-white animate-spin';
        if (s.success) return 'bg-green-600 text-white';
        if (s.success === false) return 'bg-red-600 text-white';
      }
      return 'bg-gray-300 text-gray-600';
    },
    showCheck(step) {
      const s = this.status['step' + step.id];
      return s && !s.loading && s.success && step.id < this.activeStep;
    },
    showSpinner(step) {
      const s = this.status['step' + step.id];
      return s && s.loading && step.id === this.activeStep;
    },
    showError(step) {
      const s = this.status['step' + step.id];
      return s && !s.loading && s.success === false && step.id === this.activeStep;
    },
    stepHasError(step) {
      const s = this.status['step' + step.id];
      return s && !s.loading && s.error && s.error.length > 0;
    }
  },
  created() {
    this.countryOptions = this.$isoCountriesLanguages.getCountries(this.$i18n.locale);
  }
});
</script>